import isFunction from 'lodash/isFunction';
import { Button, ButtonProps } from 'primereact/button';
import React, { type ReactNode, useCallback } from 'react';
import { SegmentEventTypes } from '../../data/tracking';
import UpgradeIconPopover from '../../subscriptionPlan/UpgradeIconPopover.component';
import Analytics from '../../utils/Analytics';
import { WrappedButtonType } from './WrappedButtonType.enum';

export { WrappedButtonType };

type WrappedButtonProps = ButtonProps & {
	className?: string;
	buttonType?: (typeof WrappedButtonType)[keyof typeof WrappedButtonType];
	additionalIconClassName?: string;
	segmentvalue?: string;
	iconPos?: ButtonProps['iconPos'];
	dataqa?: string;
	showUpgradeIcon?: boolean;
	upgradePopoverContent?: { title: string; message: ReactNode };
	upgradePopoverActions?: any;
};

/**
 * WrappedButton is a wrapper around the PrimeReact Button component.
 * It adds analytics tracking and adds a convenient way to add an icon.
 */
export const WrappedButton: React.FC<WrappedButtonProps> = props => {
	const {
		className = '',
		// TODO: should really be 'data-qa'...
		dataqa: dataQa = 'ui-button-common',
		buttonType = WrappedButtonType.Primary,
		additionalIconClassName,
		onClick,
		segmentvalue,
		showUpgradeIcon = false,
		upgradePopoverContent,
		upgradePopoverActions,
		...rest
	} = props;

	const clickHandler = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			Analytics.trackSegmentEvent({
				trackingData: {
					field_type: 'button',
					field_name_dataqa: `ui-button-${dataQa}`,
					field_value: segmentvalue,
				},
				eventType: SegmentEventTypes.UserInteraction,
			});

			if (isFunction(onClick)) {
				onClick(e);
			}
		},
		// TODO: enable react-hooks/exhaustive-deps in eslint config
		[onClick, dataQa, segmentvalue]
	);

	// Render custom label with upgrade icon (with popover if content provided,
	// otherwise a plain icon — e.g. when the whole button triggers the upgrade
	// flow and the popover lives elsewhere).
	const hasUpgradeLabel = showUpgradeIcon && rest.label;

	return (
		<div className={`common-ui-button ${className}`} data-qa={dataQa}>
			<Button
				{...rest}
				label={hasUpgradeLabel ? undefined : rest.label}
				className={`ui-button-${buttonType}`}
				data-qa={`ui-button-${dataQa}`}
				onClick={clickHandler}
				loadingIcon={
					<div
						className="spinner-image"
						role="img"
						aria-label="Loading content"
						aria-live="polite"
					/>
				}
			>
				{hasUpgradeLabel && (
					<span className="button-label-with-icon">
						{upgradePopoverContent ? (
							<UpgradeIconPopover
								content={upgradePopoverContent}
								actions={upgradePopoverActions}
								triggerClassName="button-upgrade-icon"
								placement="bottom"
							/>
						) : (
							<span className="button-upgrade-icon" />
						)}
						{rest.label}
					</span>
				)}
				{additionalIconClassName && (
					<span
						className={`additional-button-icon ${additionalIconClassName}`}
					/>
				)}
			</Button>
		</div>
	);
};
